import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import { useToast } from "src/hooks/use-toast";
import { useTutorById } from "src/services/tutorsService";
import { ROUTES } from "src/routes/RouteConfig";

// Import booking components
import BookingStepIndicator from "./booking/components/BookingStepIndicator";
import BookingStepOne from "./booking/components/BookingStepOne";
import BookingStepTwo from "./booking/components/BookingStepTwo";
import BookingStepThree from "./booking/components/BookingStepThree";
import BookingStepFour from "./booking/components/BookingStepFour";

// Import hooks and constants
import { useBookingForm } from "./booking/hooks/useBookingForm";
import { useBookingSubmission } from "./booking/hooks/useBookingSubmission";
import { BOOKING_STEPS, BookingStep } from "./booking/constants";
import { BookingSubmissionData } from "./booking/types";
import { StudentParentFormData } from "src/components/forms/StudentParentForm";

const BookDemoBySearch = () => {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Fetch tutor data
  const { tutor, isLoading: tutorLoading, error: tutorError } = useTutorById(tutorId || "");

  // Form state management
  const { formData, userFormData, updateFormData, updateUserFormData } = useBookingForm();
  
  // Submission handling
  const { submitBooking, isSubmitting } = useBookingSubmission();

  // Current step state
  const [currentStep, setCurrentStep] = useState<BookingStep>(BOOKING_STEPS.CHOOSE_TIME);

  // Handle tutor not found or error
  useEffect(() => {
    if (tutorError || (!tutorLoading && !tutor && tutorId)) {
      navigate(ROUTES.TUTORS.path);
    }
  }, [tutor, tutorError, tutorLoading, tutorId, navigate]);

  // Handle pre-filling data from login redirect
  useEffect(() => {
    if (location.state?.bookingData) {
      const {
        date: savedDate,
        timeSlot: savedTimeSlot,
        duration: savedDuration,
        topic: savedTopic,
        notes: savedNotes,
      } = location.state.bookingData;

      // Restore the saved booking data
      updateFormData({
        date: savedDate ? new Date(savedDate) : undefined,
        timeSlot: savedTimeSlot || "",
        duration: savedDuration || "60",
        topic: savedTopic || "",
        notes: savedNotes || "",
      });

      // Go directly to step 3 (user info step)
      setCurrentStep(BOOKING_STEPS.USER_INFO);

      // Clear the state after using it
      window.history.replaceState({}, document.title);
    }
  }, [location.state, updateFormData]);

  // Navigation handlers
  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, BOOKING_STEPS.CONFIRMATION));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, BOOKING_STEPS.CHOOSE_TIME));
  };

  // Handle step-specific navigation
  const handleStepOneNext = () => {
    // Validate step one data
    if (!formData.date || !formData.timeSlot || !formData.duration || !formData.topic) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    handleNext();
  };

  const handleStepTwoNext = () => {
    // Move to user info step
    handleNext();
  };

  const handleStepThreeNext = async () => {
    // Validate user info
    if (!userFormData.parentName || !userFormData.studentName || !userFormData.email) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    // Submit booking
    if (!tutor || !formData.date) {
      toast({
        title: "Error",
        description: "Missing tutor or date information.",
        variant: "destructive",
      });
      return;
    }

    const bookingData: BookingSubmissionData = {
      tutorId: tutor.id,
      date: formData.date,
      timeSlot: formData.timeSlot,
      duration: formData.duration,
      topic: formData.topic,
      notes: formData.notes,
      timezone: formData.timezone,
      userInfo: userFormData,
    };

    const success = await submitBooking(bookingData, tutor);
    if (success) {
      handleNext(); // Move to confirmation step
    }
  };

  // Render current step
  const renderCurrentStep = () => {
    if (!tutor) return null;

    const commonProps = {
      tutor,
      formData,
      userFormData,
      onBack: handleBack,
      onFormDataChange: updateFormData,
      onUserFormDataChange: updateUserFormData,
      isLoading: isSubmitting,
    };

    switch (currentStep) {
      case BOOKING_STEPS.CHOOSE_TIME:
        return (
          <BookingStepOne
            {...commonProps}
            onNext={handleStepOneNext}
          />
        );
      case BOOKING_STEPS.REVIEW:
        return (
          <BookingStepTwo
            {...commonProps}
            onNext={handleStepTwoNext}
          />
        );
      case BOOKING_STEPS.USER_INFO:
        return (
          <BookingStepThree
            {...commonProps}
            onNext={handleStepThreeNext}
          />
        );
      case BOOKING_STEPS.CONFIRMATION:
        return (
          <BookingStepFour
            {...commonProps}
            onNext={() => {}} // No next step after confirmation
          />
        );
      default:
        return (
          <BookingStepOne
            {...commonProps}
            onNext={handleStepOneNext}
          />
        );
    }
  };

  // Loading state
  if (tutorLoading || !tutor) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <p>Loading...</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <BookingStepIndicator currentStep={currentStep} />
          {renderCurrentStep()}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookDemoBySearch;
